import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../../api/client';
import type { NotifyApi } from '../../components/notifications';
import { AiCombo } from '../../components/AiCombo';
import { CountryDetailModal } from './CountryDetailModal';
import { StateDetailModal } from './StateDetailModal';
import { CityDetailModal } from './CityDetailModal';
import { useModalKeys } from '../../hooks/useModalKeys';
import { useConfirm } from '../../components/confirm';
import { serverMessage } from '../../components/confirm/serverMessage';
import '../accounting/accounting.css';
import { MaintenanceDetailFrame, type MaintenanceDetailWiring } from '../maintenance/MaintenanceDetailFrame';
import { SrBody } from '../maintenance/srLayout';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "{Name} - Contact" detail dialog — the FULL contact form opened by the
// SECOND row command on the Contacts / Invoicing Contact grid (and by New).
// Replaces the earlier 8-field mini popup, which could only edit a fraction of
// dbo.Contact and therefore silently reset nothing but also exposed nothing.
//
// Field set = every column ClientContactInput can persist:
//   First* / Middle / Last / Gender / Birthday / Client (read-only) / Prospect /
//   Position / Account / Email* / Email CC / Email BCC / Office / Home / Mobile /
//   Other Phone / Fax / Street 1 / Street 2 / Country → State → City / Zip /
//   Comment / Sample Receipt Notification / Report Delivery.
// GoLive also shows Retired Date and Photo on this dialog; neither exists on the
// client-contact write contract yet, so they are deliberately omitted rather
// than rendered as controls that could not save.
//
// Behaviours ported from the legacy BO (Contact.cs):
//  • Prospect ticked HIDES the Client row (Appearance rules, lines 41-44).
//  • Country → State → City cascade with DataSourcePropertyIsNullMode
//    .SelectNothing: a child is greyed and lists nothing until its parent is set,
//    and its New/Edit (✎) button only appears once the parent has a value.
// ─────────────────────────────────────────────────────────────────────────────

interface Geo { id: string; name: string; parentId: string | null }

interface ContactForm {
  firstName: string; middleName: string; lastName: string;
  gender: string; birthday: string;
  position: string; account: string; prospect: boolean; crmProspectId: string;
  email: string; emailCc: string; emailBcc: string;
  officePhone: string; homePhone: string; mobilePhone: string; otherPhone: string; fax: string;
  street1: string; street2: string;
  countryId: string; stateId: string; cityId: string; zip: string;
  comment: string;
  sampleReceiptNotification: boolean; reportDelivery: boolean; invoiceDelivery: boolean; photo: string;
  retiredDate: string;
}
const EMPTY: ContactForm = {
  firstName: '', middleName: '', lastName: '', gender: '', birthday: '',
  position: '', account: '', prospect: false, crmProspectId: '',
  email: '', emailCc: '', emailBcc: '',
  officePhone: '', homePhone: '', mobilePhone: '', otherPhone: '', fax: '',
  street1: '', street2: '', countryId: '', stateId: '', cityId: '', zip: '',
  comment: '', sampleReceiptNotification: true, reportDelivery: false, invoiceDelivery: false, photo: '',
  retiredDate: '',
};
type TextKey =
  | 'firstName' | 'middleName' | 'lastName' | 'position' | 'account'
  | 'email' | 'emailCc' | 'emailBcc'
  | 'officePhone' | 'homePhone' | 'mobilePhone' | 'otherPhone' | 'fax'
  | 'street1' | 'street2' | 'zip';

/** Page-mode wiring supplied by the Maintenance list (breadcrumb, prev / next, New · Delete · Refresh · Audit). */
export type ContactDetailPage = MaintenanceDetailWiring;

/** Stands in for the client segment of the contact routes when the contact has none (a Prospect contact) — the API ignores it. */
const NO_CLIENT = '00000000-0000-0000-0000-000000000000';

export function ContactDetailModal({ clientId, clientName, type: typeProp, contactOid, onClose, onSaved, notify, showClientOnNew = false, page }: {
  clientId: string;
  clientName: string;
  /** OPT-IN: show `clientName` on a NEW contact too. The contact is always filed
   *  under `clientId`; GoLive's Client form shows N/A until the save, but a quote
   *  opening this form for its own client shows that client, so the user can see
   *  where the contact will go (browser walkthrough 2026-10-20). Unset = unchanged. */
  showClientOnNew?: boolean;
  type: number;                    // 0 Report Contact · 1 Invoice Contact
  contactOid: string | null;       // null = New
  onClose: () => void;
  onSaved: (savedOid?: string) => void;
  notify: NotifyApi;
  /** When set the editor renders as the full-page Detail View (Maintenance ▸ Contacts) instead of a popup. */
  page?: ContactDetailPage;
}) {
  // The Maintenance page lets the user choose Report / Invoice (G5's Type radio) and the Client; a popup keeps its caller's.
  const [pageType, setPageType] = useState(typeProp);
  const type = page ? pageType : typeProp;
  const [pickedClient, setPickedClient] = useState(clientId);
  const [clientOpts, setClientOpts] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (!page) return;
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => setClientOpts(((r.data as { data?: { oid: string; name: string; address: string }[] }).data ?? [])
        .map(c => ({ value: c.oid, label: [c.name, c.address].filter(Boolean).join(' — ') }))))
      .catch(() => { /* the Client list stays empty */ });
  }, [page]);
  const routeClient = clientId || NO_CLIENT;
  const [f, setF] = useState<ContactForm>(EMPTY);
  const [loading, setLoading] = useState(!!contactOid);
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(false);
  const [countries, setCountries] = useState<Geo[]>([]);
  const [states, setStates] = useState<Geo[]>([]);
  const [cities, setCities] = useState<Geo[]>([]);
  // G5 Contact.CRMProspect: while Prospect is ticked the Client row is this lookup over the open CRM Prospects (Maintenance page only).
  const [prospects, setProspects] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!page) return;
    void api.get('/api/v1/clients/lookups/contact-prospects')
      .then(r => setProspects(((r.data as { data?: { id: string; name: string }[] }).data ?? []).map(o => ({ id: o.id.toLowerCase(), name: o.name }))))
      .catch(() => { /* the lookup stays empty */ });
  }, [page]);
  // 'new' opens a blank create form, 'edit' opens the selected record (GoLive
  // LookupPropertyEditor New vs Edit).
  const [countryOpen, setCountryOpen] = useState<false | 'new' | 'edit'>(false);
  const [stateOpen, setStateOpen] = useState<false | 'new' | 'edit'>(false);
  const [cityOpen, setCityOpen] = useState<false | 'new' | 'edit'>(false);
  const photoRef = useRef<HTMLInputElement>(null);
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });

  const p = type === 1 ? 'Invoice' : 'Report';

  const loadGeo = useCallback(async () => {
    try {
      const [cs, st, ci] = await Promise.all([
        api.get('/api/v1/system/states/countries'),
        api.get('/api/v1/cities/states'),
        api.get('/api/v1/cities'),
      ]);
      setCountries(((cs.data as { data?: { id: string; name: string }[] }).data ?? []).map(c => ({ id: c.id, name: c.name, parentId: null })));
      setStates(((st.data as { data?: { id: string; shortName: string; longName: string | null; countryId: string | null }[] }).data ?? [])
        .map(s => ({ id: s.id, name: s.longName || s.shortName, parentId: s.countryId })));
      setCities(((ci.data as { data?: { id: string; cityName: string | null; stateId: string | null }[] }).data ?? [])
        .map(c => ({ id: c.id, name: c.cityName ?? '', parentId: c.stateId })));
    } catch { /* geo dropdowns are optional */ }
  }, []);
  useEffect(() => { void loadGeo(); }, [loadGeo]);

  // Seed an edit from the FULL record (the grid row is only a subset).
  useEffect(() => {
    // G5's New Contact starts with Sample Receipt Notification off (the popup on a client keeps its own default).
    if (!contactOid) { setF({ ...EMPTY, sampleReceiptNotification: !page }); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    void api.get(`/api/v1/clients/${encodeURIComponent(routeClient)}/contacts/${contactOid}`)
      .then(r => {
        if (cancelled) return;
        const d = (r.data as { data?: Record<string, unknown> }).data ?? {};
        const s = (k: string) => String(d[k] ?? '');
        if (page) { setPageType(Number(d.type) === 1 ? 1 : 0); setPickedClient(s('clientId').toLowerCase()); }
        setF({
          firstName: s('firstName'), middleName: s('middleName'), lastName: s('lastName'),
          gender: d.gender === null || d.gender === undefined ? '' : String(d.gender),
          birthday: s('birthday').slice(0, 10),
          position: s('position'), account: s('account'), prospect: !!d.prospect, crmProspectId: s('crmProspectId').toLowerCase(),
          email: s('email'), emailCc: s('emailCc'), emailBcc: s('emailBcc'),
          officePhone: s('officePhone'), homePhone: s('homePhone'), mobilePhone: s('mobilePhone'),
          otherPhone: s('otherPhone'), fax: s('fax'),
          street1: s('street1'), street2: s('street2'),
          countryId: s('countryId'), stateId: s('stateId'), cityId: s('cityId'), zip: s('zip'),
          comment: s('comment'),
          sampleReceiptNotification: !!d.sampleReceiptNotification, reportDelivery: !!d.reportDelivery, invoiceDelivery: !!d.invoiceDelivery, photo: s('photo'),
          retiredDate: s('retiredDate').slice(0, 10),
        });
      })
      .catch(() => { if (!cancelled) notify.error('Failed to load the contact.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactOid, routeClient, notify, page]);

  const { confirm } = useConfirm();
  // The write itself — throws on failure. Shared by the popup's OK and the page's Save set, so a contact is stored one way.
  const persist = useCallback(async (): Promise<string | undefined> => {
    const body = {
      firstName: f.firstName, middleName: f.middleName, lastName: f.lastName,
      gender: f.gender === '' ? null : Number(f.gender),
      birthday: f.birthday || null,
      email: f.email, emailCc: f.emailCc, emailBcc: f.emailBcc,
      officePhone: f.officePhone, homePhone: f.homePhone, mobilePhone: f.mobilePhone,
      otherPhone: f.otherPhone, fax: f.fax,
      street1: f.street1, street2: f.street2,
      countryId: f.countryId || null, stateId: f.stateId || null, cityId: f.cityId || null,
      zip: f.zip, position: f.position, account: f.account, prospect: f.prospect,
      comment: f.comment, type,
      sampleReceiptNotification: f.sampleReceiptNotification, reportDelivery: f.reportDelivery, invoiceDelivery: f.invoiceDelivery, photo: f.photo,
      retiredDate: f.retiredDate || null,
      // Sent from the Maintenance page only: "" clears, an id files the contact under that Prospect; other callers leave the stored value.
      ...(page ? { crmProspectId: f.crmProspectId, customerId: pickedClient } : {}),
    };
    let createdOid: string | undefined;
    if (contactOid) await api.put(`/api/v1/clients/${encodeURIComponent(routeClient)}/contacts/${contactOid}`, body);
    else {
      // A contact with no client (a Prospect contact) is created through the standalone route.
      const owner = page ? pickedClient : clientId;
      const res = await api.post(owner ? `/api/v1/clients/${encodeURIComponent(owner)}/contacts` : '/api/v1/clients/contacts', body);
      createdOid = (res.data as { data?: { oid?: string } }).data?.oid;
    }
    return createdOid ?? contactOid ?? undefined;
  }, [f, type, contactOid, clientId, routeClient, page, pickedClient]);

  const save = useCallback(async () => {
    // GoLive requires only First Name (Contact.cs has [RuleRequiredField] on
    // FirstName alone; Email carries no required rule), so Email is optional here.
    if (!f.firstName.trim()) { notify.warning('First Name is required.'); return; }
    // Every save is confirmed through the shared dialog (standing rule 7), the
    // same question the Client form asks.
    if (!(await confirm({ title: 'Save', message: 'Do you want to Save?' }))) return;
    setSaving(true);
    try {
      const id = await persist();
      notify.success('Saved successfully.');
      onSaved(contactOid ? undefined : id);
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save the contact.'); }
    finally { setSaving(false); }
  }, [f, contactOid, notify, onSaved, confirm, persist]);

  // ── field helpers (same sizing/left-aligned labels as the other forms) ──
  const input = (highlight: boolean): CSSProperties => ({
    width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5,
    boxSizing: 'border-box', fontSize: 12, background: highlight ? '#fffbe7' : '#fff',
  });
  // Sample Registration layout on the Maintenance page: <label> + control are direct children of the .sr-fgrid column.
  const row = (label: string, control: ReactNode, req = false) => page ? (
    <><label className={req ? 'req' : undefined}>{label}</label>{control}</>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      <label style={{ fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap', color: '#4a4f58' }}>{label}{label ? ':' : ''}{req && <span style={{ color: '#d33' }}>*</span>}</label>
      {control}
    </div>
  );
  const text = (key: TextKey, req = false): ReactNode => (
    <input type="text" value={f[key]} onChange={e => setF(prev => ({ ...prev, [key]: e.target.value }))}
           style={input(req && !f[key].trim())} />
  );
  // Cascading geo lookup: greyed + empty until the parent is set, and the New (+)
  // button only appears once the parent has a value (GoLive reveals it one level
  // at a time). New (ti-plus) opens a BLANK create form even when a value is
  // already selected — GoLive shows "New", not "Edit", on these lookups.
  const geoRow = (opts: Geo[], value: string, onChange: (v: string) => void,
                  parentVal: string | undefined, onNew: () => void, gated = false): ReactNode => (
    <AiCombo
      value={value}
      options={(gated ? [] : opts.filter(o => parentVal === undefined || o.parentId === parentVal))
        .map(o => ({ value: o.id, label: o.name }))}
      onChange={v => onChange(v ?? '')}
      onNew={gated ? undefined : onNew}
      newTitle="New"
      disabled={gated}
      erasable
    />
  );
  const check = (label: string, on: boolean, set: (v: boolean) => void) => (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => set(e.target.checked)} /> {label}
    </label>
  );

  const bdayStyle = <style>{'.cdm-bday.cdm-bday-empty:not(:focus)::-webkit-datetime-edit{color:transparent}'}</style>;

  const bodyEl = loading ? (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
    ) : (
      <SrBody page={!!page} title="Contact Information" icon="👤" popupStyle={{ padding: 16, overflowY: 'auto' }}>
        <div className={page ? 'maint-cols' : undefined} style={page ? undefined : { display: 'flex', gap: 22 }}>
          <div className={page ? 'sr-fgrid maint-fg2' : undefined} style={page ? undefined : { flex: 1 }}>
            {row('Full Name', <input type="text" readOnly value={`${f.firstName} ${f.middleName} ${f.lastName}`.replace(/\s+/g, ' ').trim()}
                                     style={{ ...input(false), background: '#f3f4f6', color: '#4a4f58' }} />)}
            {row('First Name', text('firstName', true), true)}
            {/* G5 Contact.Type (Report / Invoice) — a radio on the Maintenance detail, fixed by the tab in a popup. */}
            {page && row('Type', (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12 }}>
                {([[0, 'Report'], [1, 'Invoice']] as const).map(([v, label]) => (
                  <label key={v} style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="radio" name="contact-type" checked={pageType === v} onChange={() => setPageType(v)} /> {label}
                  </label>
                ))}
              </div>
            ))}
            {row('Middle Name', text('middleName'))}
            {row('Last Name', text('lastName'))}
            {row('Gender', (
              <AiCombo value={f.gender || null} options={[{ value: '1', label: 'Male' }, { value: '2', label: 'Female' }]}
                       onChange={v => setF(p2 => ({ ...p2, gender: v ?? '' }))} placeholder="None" erasable />
            ))}
            {row('Birthday', <input type="date" className={f.birthday ? 'cdm-bday' : 'cdm-bday cdm-bday-empty'}
                                    value={f.birthday} onChange={e => setF(p2 => ({ ...p2, birthday: e.target.value }))} style={input(false)} />)}
            {/* Client is fixed to the client being edited, and GoLive HIDES it
                entirely while Prospect is ticked (Contact.cs Appearance rules). */}
            {/* GoLive shows N/A on a NEW contact (the client link is set on save
                from the parent client, not typed here); an existing contact shows
                its client. Hidden entirely while Prospect is ticked. */}
            {!f.prospect && !page && row('Client', <input type="text" readOnly value={contactOid || showClientOnNew ? (clientName || 'N/A') : 'N/A'}
                                                 style={{ ...input(false), background: '#f3f4f6', color: '#4a4f58' }} />)}
            {/* On the Maintenance detail the Client is G5's lookup — it chooses the client on New and moves the contact on Edit. */}
            {!f.prospect && page && row('Client', (
              <AiCombo value={pickedClient || null} options={clientOpts} onChange={v => setPickedClient(v ?? '')} placeholder="N/A" erasable />
            ))}
            {/* G5 swaps the read-only Client for the CRMProspect lookup (same caption) while Prospect is ticked. */}
            {page && f.prospect && row('Client', (
              <AiCombo value={f.crmProspectId || null} placeholder="N/A" erasable
                       options={[
                         ...prospects.map(o => ({ value: o.id, label: o.name })),
                         ...(f.crmProspectId && !prospects.some(o => o.id === f.crmProspectId) ? [{ value: f.crmProspectId, label: '(selected prospect)' }] : []),
                       ]}
                       onChange={v => setF(p2 => ({ ...p2, crmProspectId: v ?? '' }))} />
            ))}
            {row('', check('Prospect', f.prospect, v => setF(p2 => ({ ...p2, prospect: v }))))}
            {row('Position', text('position'))}
            {row('Account', text('account'))}
            {/* GoLive labels this "Email" (not "Report/Invoice Mail") and it is
                NOT required — only First Name is. */}
            {row('Email', text('email'))}
            {row('Email CC', text('emailCc'))}
            {row('Email BCC', text('emailBcc'))}
            {row('Office Phone', text('officePhone'))}
            {page ? row('Home Phone', text('homePhone')) : row('Mobile Phone', text('mobilePhone'))}
            {/* G5's detail lists all three flags, whatever the Type. */}
            {page && row('', check('Sample Receipt Notification', f.sampleReceiptNotification, v => setF(p2 => ({ ...p2, sampleReceiptNotification: v }))))}
            {page && row('', check('Report Delivery', f.reportDelivery, v => setF(p2 => ({ ...p2, reportDelivery: v }))))}
            {page && row('', check('Invoice Delivery', f.invoiceDelivery, v => setF(p2 => ({ ...p2, invoiceDelivery: v }))))}
          </div>
          <div className={page ? 'sr-fgrid maint-fg2' : undefined} style={page ? undefined : { flex: 1 }}>
            {page ? row('Mobile Phone', text('mobilePhone')) : row('Home Phone', text('homePhone'))}
            {row('Other Phone', text('otherPhone'))}
            {row('Fax', text('fax'))}
            {row('Street 1', text('street1'))}
            {row('Street 2', text('street2'))}
            {row('Country', geoRow(countries, f.countryId, v => setF(p2 => ({ ...p2, countryId: v, stateId: '', cityId: '' })), undefined, () => setCountryOpen('new')))}
            {row('State', geoRow(states, f.stateId, v => setF(p2 => ({ ...p2, stateId: v, cityId: '' })), f.countryId, () => setStateOpen('new'), !f.countryId))}
            {row('City', geoRow(cities, f.cityId, v => setF(p2 => ({ ...p2, cityId: v })), f.stateId, () => setCityOpen('new'), !f.countryId || !f.stateId))}
            {row('Zip', text('zip'))}
            {/* Retired Date — GoLive dbo.Contact.RetiredDate (right column, after
                Zip). Blank when unset (calendar dropdown only); no AM/PM. */}
            {/* G5's NEW Invoice Contact popup has no Retired Date (a Report contact, or an existing one, keeps it). */}
            {!(type === 1 && !contactOid) && row('Retired Date', <input type="date" className={f.retiredDate ? 'cdm-bday' : 'cdm-bday cdm-bday-empty'}
                                        value={f.retiredDate} onChange={e => setF(p2 => ({ ...p2, retiredDate: e.target.value }))} style={input(false)} />)}
            {/* Photo — GoLive XAF ImageEditor (dbo.Contact.Photo varbinary):
                empty shows a readonly box + Browse; a chosen image becomes a
                preview with edit/erase. Reuses the shared .cty-flag styles.
                f.photo holds a raw base64 (round-tripped from the API byte[]) or
                a data URL (fresh upload); both are accepted by server ParsePhoto. */}
            <div style={{ display: "grid", gridTemplateColumns: "104px 1fr", alignItems: "start", gap: 8, marginBottom: 7 }}>
              <label style={{ fontSize: 11, color: "#4a4f58" }}>Photo{page ? '' : ':'}</label>
              <div>
                <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }}
                       onChange={e => { const fl = e.target.files?.[0]; e.target.value = ""; if (!fl) return;
                         const rd = new FileReader(); rd.onload = () => setF(p2 => ({ ...p2, photo: String(rd.result) })); rd.readAsDataURL(fl); }} />
                {f.photo ? (
                  <div>
                    <img className="cty-flag-lg" src={f.photo.startsWith("data:") ? f.photo : `data:image;base64,${f.photo}`} alt="Contact photo" />
                    <div className="cty-flag-acts">
                      <button type="button" className="cty-flag-btn" title="Edit / choose image" onClick={() => photoRef.current?.click()}><i className="ti ti-pencil" /></button>
                      <button type="button" className="cty-flag-btn" title="Erase" onClick={() => setF(p2 => ({ ...p2, photo: "" }))}><i className="ti ti-eraser" /></button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 4 }}>
                    <input type="text" readOnly value="" onClick={() => photoRef.current?.click()} style={{ ...input(false), background: "#f3f4f6", color: "#4a4f58", cursor: "pointer" }} />
                    <button type="button" className="ca-btn ca-btn-solid" style={{ whiteSpace: "nowrap" }} onClick={() => photoRef.current?.click()}>Browse…</button>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', alignItems: 'start', gap: 8, marginBottom: 7 }}>
              <label style={{ fontSize: 11, color: '#4a4f58' }}>Comment{page ? '' : ':'}</label>
              <textarea rows={4} value={f.comment} onChange={e => setF(p2 => ({ ...p2, comment: e.target.value }))}
                        style={{ ...input(false), resize: 'vertical' }} />
            </div>
          </div>
        </div>
        {!page && <div style={{ display: 'flex', gap: 24, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
          {/* GoLive splits these by tab: the Contacts (Report) dialog shows
              Sample Receipt Notification + Report Delivery, while the Invoicing
              Contact dialog shows a SINGLE "Invoice Delivery" box
              (dbo.Contact.InvoiceDelivery). AI previously showed the Report pair
              on both, so the Invoicing tab wrote the wrong flags entirely. */}
          {type === 1
            ? check("Invoice Delivery", f.invoiceDelivery, v => setF(p2 => ({ ...p2, invoiceDelivery: v })))
            : <>
                {check("Sample Receipt Notification", f.sampleReceiptNotification, v => setF(p2 => ({ ...p2, sampleReceiptNotification: v })))}
                {check("Report Delivery", f.reportDelivery, v => setF(p2 => ({ ...p2, reportDelivery: v })))}
              </>}
        </div>}
      </SrBody>
    );

  const nestedEl = (
    <>
      {countryOpen && <CountryDetailModal countryId={countryOpen === 'edit' ? (f.countryId || null) : null} onClose={() => setCountryOpen(false)}
        onSaved={async id => { setCountryOpen(false); await loadGeo(); setF(p2 => ({ ...p2, countryId: id, stateId: '', cityId: '' })); }} notify={notify} />}
      {stateOpen && <StateDetailModal stateId={stateOpen === 'edit' ? (f.stateId || null) : null} countryId={f.countryId || null} onClose={() => setStateOpen(false)}
        onSaved={async id => { setStateOpen(false); await loadGeo(); setF(p2 => ({ ...p2, stateId: id, cityId: '' })); }} notify={notify} />}
      {cityOpen && <CityDetailModal cityId={cityOpen === 'edit' ? (f.cityId || null) : null} stateId={f.stateId || null} onClose={() => setCityOpen(false)}
        onSaved={async id => { setCityOpen(false); await loadGeo(); setF(p2 => ({ ...p2, cityId: id })); }} notify={notify} />}
    </>
  );

  // Full-page Detail View (standing rule 12): the Maintenance list is REPLACED by this page.
  if (page) {
    const who = `${f.firstName} ${f.lastName}`.trim();
    return (
      <MaintenanceDetailFrame
        entityLabel={`${p} Contact`} recordName={who || (contactOid ? '' : 'New')} listTitle={page.listTitle}
        loading={loading} formName="Client Master Data" isNew={!contactOid}
        validate={() => (f.firstName.trim() ? null : 'First Name is required.')}
        persist={persist} onAfterSave={onSaved} onNew={page.onNew} actions={page}
        onHome={page.onHome} onClose={onClose}
        prevId={page.prevId} nextId={page.nextId} onNavigate={page.onNavigate}
        suspendKeys={!!(countryOpen || stateOpen || cityOpen)}
      >
        {bdayStyle}
        {bodyEl}
        {nestedEl}
      </MaintenanceDetailFrame>
    );
  }

  return (
    // Backdrop closes only on a DIRECT click — a bubbled click from a nested
    // Country/State/City popup's close-X must not close this Contact form.
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
           style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 760, maxWidth: '96vw',
                    height: max ? '94vh' : undefined, maxHeight: '94vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        {bdayStyle}
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                      color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{`${`${f.firstName} ${f.lastName}`.trim() || `New ${p} Contact`} - Contact`}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>

        {bodyEl}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving || loading} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {nestedEl}
    </div>
  );
}
